import type { DateTime } from 'luxon';
import { allocationBorderWidthInRem, allocationBoxHeightInRem, columnWidthInRem } from '../sharedStyles';

export type LayoutAlgorithm = 'Allocation-Variant-Order' | 'Compact';

export type AllocationLayoutData = {
    id: string;
    startDateTime: DateTime;
    endDateTime: DateTime;
    allocationVariantIndex: number;
};
type AllocationWithRowIndex = AllocationLayoutData & { rowIndex: number };

export type AllocationPosition = {
    left: number;
    top: number;
};

export type LayoutResult = {
    positionedAllocations: Record<string, AllocationPosition>;
    rowHeight: number;
};

export function calculatePositions(
    allocationsWithRows: AllocationWithRowIndex[],
    timelineStartDateTime: DateTime,
    isFirstRow: boolean,
    totalRows: number,
): LayoutResult {
    const positionedAllocations: Record<string, AllocationPosition> = {};
    const columnBorderWidthInRem = 0.0625; // 1px
    const gapBetweenAllocations = 0.0625; // 1px

    allocationsWithRows.forEach((allocation) => {
        // Use the latter of allocation start or timeline start to handle allocations that begin before the visible range
        const visibleStartDateTime =
            allocation.startDateTime < timelineStartDateTime ? timelineStartDateTime : allocation.startDateTime;
        const startDays = visibleStartDateTime.diff(timelineStartDateTime, 'days').days;
        const left = startDays * (columnWidthInRem + columnBorderWidthInRem);
        const top =
            (isFirstRow ? 0.0625 : 0) +
            allocation.rowIndex * (allocationBoxHeightInRem + gapBetweenAllocations) +
            0.0625;

        positionedAllocations[allocation.id] = { left, top };
    });

    const topPadding = isFirstRow ? 0.125 : 0.0625; // 2px or 1px
    const gapsBetweenRows = Math.max(0, totalRows - 1) * gapBetweenAllocations; // 1px gap between allocations
    const bottomPadding = 0.0625; // 1px

    const rowHeight = topPadding + totalRows * allocationBoxHeightInRem + gapsBetweenRows + bottomPadding;

    return { positionedAllocations, rowHeight };
}

export function layoutByAllocationVariantOrder(
    allocations: AllocationLayoutData[],
    timelineStartDateTime: DateTime,
    isFirstRow: boolean,
): LayoutResult {
    // Sort allocations by allocationVariantIndex (ascending)
    const sortedAllocations = [...allocations].sort((a, b) => a.allocationVariantIndex - b.allocationVariantIndex);

    // Assign row indices based on array position
    const allocationsWithRows: AllocationWithRowIndex[] = sortedAllocations.map((allocation, rowIndex) => ({
        ...allocation,
        rowIndex,
    }));

    const totalRows = sortedAllocations.length;

    return calculatePositions(allocationsWithRows, timelineStartDateTime, isFirstRow, totalRows);
}

type RowInfo = {
    rowIndex: number;
    endDateTime: DateTime;
};

// This requires all allocations to be pre-sorted by startDateTime ascending (!)
export function assignCompactRows(allocations: AllocationLayoutData[]): {
    allocationsWithRows: AllocationWithRowIndex[];
    totalRows: number;
} {
    const allocationsWithRows: AllocationWithRowIndex[] = [];
    const rows: RowInfo[] = [];

    for (const allocation of allocations) {
        let firstFreeRow: RowInfo | null = null;
        let firstFreeRowIndex = -1;

        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];

            // Check if row is free (endDate <= startDate, using half-open intervals)
            if (row.endDateTime <= allocation.startDateTime) {
                // Try to find the earliest free row
                if (firstFreeRow === null) {
                    firstFreeRow = row;
                    firstFreeRowIndex = i;
                }
            }
        }

        if (firstFreeRow !== null) {
            allocationsWithRows.push({
                ...allocation,
                rowIndex: firstFreeRow.rowIndex,
            });

            // Update the end date of this row
            rows[firstFreeRowIndex].endDateTime = allocation.endDateTime;
        } else {
            // Create new row
            const newRowNumber = rows.length;

            allocationsWithRows.push({
                ...allocation,
                rowIndex: newRowNumber,
            });

            rows.push({
                rowIndex: newRowNumber,
                endDateTime: allocation.endDateTime,
            });
        }
    }

    return { allocationsWithRows, totalRows: rows.length };
}

export function layoutByCompactAlgorithm(
    allocations: AllocationLayoutData[],
    timelineStartDateTime: DateTime,
    isFirstRow: boolean,
): LayoutResult {
    const { allocationsWithRows, totalRows } = assignCompactRows(allocations);

    return calculatePositions(allocationsWithRows, timelineStartDateTime, isFirstRow, totalRows);
}

type AllocationForCreateButtons = {
    startDateTime: DateTime;
    endDateTime: DateTime;
    /** Vertical offset produced by calculatePositions; null while an allocation is not positioned yet. */
    top: number | null;
};

/**
 * Height of the spacer above each day's create button, in rem, measured from the row's top edge.
 *
 * The button fills everything below its spacer, so it covers every free slot in that day's column: two free
 * slots make a button twice as tall. A day with no allocation gets a spacer of just the row's top padding,
 * i.e. a button covering the whole cell.
 *
 * The spacer ends 1px above the lowest allocation's bottom border so the two share a single line rather than
 * drawing two adjacent ones.
 */
export function calculateCreateButtonSpacerPerDay(
    allocations: AllocationForCreateButtons[],
    timelineStartDateTime: DateTime,
    daysCount: number,
    isFirstRow: boolean,
): Map<number, number> {
    const topPaddingInRem = isFirstRow ? allocationBorderWidthInRem : 0;

    // An unpositioned allocation has no known slot, so it cannot push the button down.
    const positionedAllocations = allocations.filter((allocation) => allocation.top !== null);

    const spacerPerDay = new Map<number, number>();

    for (let dayIndex = 0; dayIndex < daysCount; dayIndex++) {
        const dayStart = timelineStartDateTime.plus({ days: dayIndex });
        const dayEnd = dayStart.plus({ days: 1 });

        // Bottom edge of the lowest allocation occupying this day.
        // Half-open intervals: an allocation ending exactly at dayStart does not occupy this day.
        let lowestBottomInRem: number | null = null;
        for (const allocation of positionedAllocations) {
            if (allocation.top !== null && allocation.startDateTime < dayEnd && allocation.endDateTime > dayStart) {
                const bottomInRem = allocation.top + allocationBoxHeightInRem;
                if (lowestBottomInRem === null || bottomInRem > lowestBottomInRem) {
                    lowestBottomInRem = bottomInRem;
                }
            }
        }

        spacerPerDay.set(dayIndex, lowestBottomInRem === null ? topPaddingInRem : lowestBottomInRem);
    }

    return spacerPerDay;
}

export function useLayoutAllocations(layoutAlgorithm: LayoutAlgorithm) {
    function layout(
        allocations: AllocationLayoutData[],
        timelineStartDateTime: DateTime,
        isFirstRow: boolean,
    ): LayoutResult {
        switch (layoutAlgorithm) {
            case 'Allocation-Variant-Order':
                return layoutByAllocationVariantOrder(allocations, timelineStartDateTime, isFirstRow);

            case 'Compact':
                return layoutByCompactAlgorithm(allocations, timelineStartDateTime, isFirstRow);

            default:
                throw new Error(`Unknown layout algorithm: ${layoutAlgorithm}`);
        }
    }

    return { layout };
}
